import React from 'react'
import { useTranslation } from 'react-i18next'
import { combinedFetchStatus } from 'mobx-document'
import { BeWizrCourse, BeWizrLearningTrack } from '~/models'
import { bewizrCoursesStore, bewizrLearningTracksStore, bewizrStore } from '~/stores'
import { observer } from '~/ui/component'
import { EmptyOrFetching, PushButton, Scroller, VBox } from '~/ui/components'
import { AppLayoutConfig } from '~/ui/layouts'
import { layout } from '~/ui/styling'
import { BeWizrSection } from '../bewizr'
import BeWizrLearningTrackTile from '../bewizr-learning-tracks/BeWizrLearningTrackTile'
import BeWizrGrid from '../bewizr/BeWizrGrid'
import BeWizrCourseTile from './BeWizrCourseTile'

const BeWizrCoursesScreen = observer('BeWizrCoursesScreen', () => {

  const [t] = useTranslation()

  const learningTracksEndpoint = bewizrLearningTracksStore.myLearningTracks
  const coursesEndpoint        = bewizrCoursesStore.myCourses
  const myFinishedCourses      = bewizrCoursesStore.myFinishedCourses
  const hasReadonlyCourses     = bewizrStore.profile?.hasNonParticipantCourses ?? false

  const fetch = React.useCallback(() => {
    learningTracksEndpoint.fetch()
    coursesEndpoint.fetch({limit: 999})
    myFinishedCourses.fetch({limit: 999})
  }, [learningTracksEndpoint, coursesEndpoint, myFinishedCourses])

  React.useEffect(() => {
    fetch()
  }, [fetch])

  const fetchStatus = combinedFetchStatus(learningTracksEndpoint.fetchStatus, coursesEndpoint.fetchStatus, myFinishedCourses.fetchStatus)

  //------
  // Rendering

  const empty = fetchStatus !== 'done' || (learningTracksEndpoint.empty && coursesEndpoint.empty && myFinishedCourses.empty)

  function render() {
    return (
      <>
        <AppLayoutConfig
          title={t('bewizr-courses:title')}
          fetchStatus={fetchStatus}
        />
        {empty ? renderEmpty() : renderBody()}
      </>
    )
  }

  function renderBody() {
    return (
      <Scroller contentPadding={layout.padding.m}>
        <VBox gap={layout.padding.xl}>
          {learningTracksEndpoint.data.length > 0 && (
            <BeWizrSection title={t('bewizr-learning-tracks:grid.title')}>
            <BeWizrGrid
              endpoint={learningTracksEndpoint}
              renderItem={renderLearningTrack}
            />
          </BeWizrSection>
          )}
          {coursesEndpoint.data.length > 0 && (
            <BeWizrSection title={t('bewizr-courses:grid.mine')}>
            <BeWizrGrid
              endpoint={coursesEndpoint}
              renderItem={renderCourse}
            />
          </BeWizrSection>
          )}
          {myFinishedCourses.data.length > 0 && (
            <BeWizrSection title={t('bewizr-courses:grid.completed')}>
            <BeWizrGrid
              endpoint={myFinishedCourses}
              renderItem={renderCourse}
            />
          </BeWizrSection>
          )}
        </VBox>
      </Scroller>
    )
  }

  function renderCourse(course: BeWizrCourse) {
    return (
      <BeWizrCourseTile
        title={course.title}
        slug={course.slug}
        image={course.links.image}
        progress={course.progress}
      />
    )
  }

  function renderLearningTrack(learningTrack: BeWizrLearningTrack) {
    return (
      <BeWizrLearningTrackTile
        title={learningTrack.title}
        id={learningTrack.id}
        image={learningTrack.links.image}
        itemCount={learningTrack.itemCount}
      />
    )
  }

  function renderEmpty() {
    return (
      <EmptyOrFetching
        {...t('bewizr-courses:empty.mine')}
        status={fetchStatus}
        flex
      >
        {hasReadonlyCourses === true &&
          <PushButton
            href='//bewizr-readonly-courses'
            caption={t('bewizr-courses:empty.mine.button')}
          />
        }
      </EmptyOrFetching>
    )
  }

  return render()

})

export default BeWizrCoursesScreen